import AddTrackingModal from '@c/modals/AddTrackingModal';
import DynamicToast from '@c/toasts/DynamicToast';
import type { ChatDocument, QuickReply } from '@models/chat';
import type { OrderItemDocument } from '@models/order';
import type { ProductDocument } from '@models/product';
import { useQuery } from '@tanstack/react-query';
import { getOrderById } from '@util/firestore/orders';
import { isDomestic } from '@util/firestore/shipengine';
import { MarkReturnAsReceivedModal } from 'app/dashboard/(dashboard)/returns/_components/MarkReturnAsReceivedModal';
import { useReturns } from 'app/support/hooks/useReturns';
import { useAuth } from 'context/AuthContext';
import { useConfirmationModal } from 'context/ConfirmationModalContext';
import { useToastContext } from 'context/ToastContext';
import { useState } from 'react';
import { pickReturnForChat } from './utils';

interface QuickRepliesProps {
  title?: string;
  quickReplies: QuickReply[];
  onQuickReply: (text: string) => void;
  isLoading?: boolean;
  product: ProductDocument | null | undefined;
  chat: ChatDocument;
  stacked?: boolean;
}

const SKELETON_WIDTHS = ['w-24', 'w-32', 'w-28'];

const RETURN_RECEIVED_OK_TEXTS = new Set([
  'Everything looks good',
  'Item arrived in good condition',
]);

const RETURN_RECEIVED_ISSUE_TEXTS = new Set([
  "Item returned, but there's an issue",
  'Item arrived damaged or not in original condition',
]);

export default function QuickReplies({
  title,
  quickReplies,
  onQuickReply,
  isLoading,
  product,
  chat,
  stacked,
}: QuickRepliesProps) {
  const { showToast } = useToastContext();
  const { confirmationModal } = useConfirmationModal();
  const { userDoc } = useAuth();
  const [pendingReply, setPendingReply] = useState<{
    text: string;
    item: OrderItemDocument;
  } | null>(null);
  const [pendingIssue, setPendingIssue] = useState<string | null>(null);

  const { data: orderDoc } = useQuery({
    queryKey: ['order', chat.order_id],
    queryFn: () => getOrderById(chat.order_id ?? ''),
    enabled: !!chat.order_id,
  });

  const isSeller = userDoc?.uid === chat.seller_id;
  const { returnsByOrder, markAsReceivedMutation } = useReturns({
    orderId: chat.order_id,
    uid: userDoc?.uid,
    field: isSeller ? 'seller_id' : 'buyer_id',
  });
  const returnDoc = pickReturnForChat(returnsByOrder, chat.id);
  const isWarehouseProduct = !!product?.integration_info;
  const effectiveReturnDoc = returnDoc
    ? { ...returnDoc, is_warehouse_return: isWarehouseProduct }
    : null;

  const handleQuickReply = (text: string) => {
    if (RETURN_RECEIVED_OK_TEXTS.has(text)) {
      if (!effectiveReturnDoc) {
        showToast(
          <DynamicToast
            text={'An error occurred. Please try again later.'}
            error
          />
        );
        return;
      }

      confirmationModal({
        message: 'Mark return as received?',
        helpText:
          'Please confirm you have reviewed all the details of the return and everything looks good. This action cannot be undone.',
        confirmText: 'Yes, mark as received',
        onConfirm: () => {
          markAsReceivedMutation.mutate(
            {
              id: effectiveReturnDoc.id,
              received_by_warehouse: 'yes',
              warehouse_comments: effectiveReturnDoc.warehouse_comments ?? '',
              images: effectiveReturnDoc.images ?? [],
              is_warehouse_return: effectiveReturnDoc.is_warehouse_return,
              order_id: effectiveReturnDoc.order_id,
              chat,
            },
            {
              onSuccess: () => onQuickReply(text),
            }
          );
        },
      });
      return;
    }

    if (RETURN_RECEIVED_ISSUE_TEXTS.has(text)) {
      if (!effectiveReturnDoc) {
        showToast(
          <DynamicToast
            text={'An error occurred. Please try again later.'}
            error
          />
        );
        return;
      }

      setPendingIssue(text);
      return;
    }

    if (text === 'I have already shipped this item') {
      if (!product || !orderDoc) {
        showToast(
          <DynamicToast
            text={'An error occurred. Please try again later.'}
            error
          />
        );
        return;
      }

      const usesMxLabel =
        product.is_flat_rate === false &&
        !isDomestic(product.shipment, orderDoc.address);

      if (usesMxLabel) {
        const allItems = Object.values(orderDoc.sellers ?? {}).flat();
        const orderHasAddress = orderDoc?.address;
        const orderItemsHaveLabel =
          allItems.length > 0 && allItems.every((item) => !!item.label_id);

        if (!orderItemsHaveLabel || !orderHasAddress) {
          showToast(
            <DynamicToast
              text={'An error occurred. Please try again later.'}
              error
            />
          );
          return;
        }

        onQuickReply(text);
        return;
      }

      const matchedItem = Object.values(orderDoc.sellers ?? {})
        .flat()
        .find((i) => i.id === product.id);

      if (!matchedItem) {
        showToast(
          <DynamicToast
            text={'An error occurred. Please try again later.'}
            error
          />
        );
        return;
      }

      if (!matchedItem.tracking_number) {
        setPendingReply({ text, item: matchedItem });
        return;
      }

      onQuickReply(text);
      return;
    }

    onQuickReply(text);
  };

  if (stacked) {
    return (
      <>
        <div className="flex flex-col items-end gap-3 py-2">
          {title && (
            <p className="text-[1.6rem] font-semibold text-zinc-900">{title}</p>
          )}
          {isLoading
            ? SKELETON_WIDTHS.slice(0, quickReplies.length || 3).map(
                (width, i) => (
                  <div
                    key={i}
                    className={`h-[4.2rem] ${width} animate-pulse rounded-full bg-zinc-200`}
                  />
                )
              )
            : quickReplies.map((reply) => (
                <button
                  key={reply.text}
                  type="button"
                  onClick={() => handleQuickReply(reply.text)}
                  className="rounded-bl-[1.8rem] rounded-tl-[1.8rem] rounded-tr-[1.8rem] bg-brand-lightest-gray px-6 py-[1.4rem] text-[1.5rem] font-semibold text-brand-black transition-colors hover:bg-brand-secondary hover:text-white"
                >
                  {reply.text}
                </button>
              ))}
        </div>
        <AddTrackingModal
          isOpen={!!pendingReply}
          dismiss={() => setPendingReply(null)}
          order={orderDoc ?? undefined}
          order_item={pendingReply?.item}
          onSuccess={() => {
            if (pendingReply) onQuickReply(pendingReply.text);
            setPendingReply(null);
          }}
        />
        {effectiveReturnDoc && (
          <MarkReturnAsReceivedModal
            isOpen={!!pendingIssue}
            dismiss={() => setPendingIssue(null)}
            returnDoc={effectiveReturnDoc}
            forceNeedsAttention
            chat={chat}
            onSuccess={() => {
              if (pendingIssue) onQuickReply(pendingIssue);
              setPendingIssue(null);
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col items-start gap-3 py-2">
        {title && (
          <p className="text-[1.4rem] font-medium text-zinc-700">{title}</p>
        )}
        <div className="flex flex-wrap gap-2">
          {isLoading
            ? SKELETON_WIDTHS.slice(0, quickReplies.length || 3).map(
                (width, i) => (
                  <div
                    key={i}
                    className={`h-[3.6rem] ${width} animate-pulse rounded-full border border-zinc-200 bg-zinc-200`}
                  />
                )
              )
            : quickReplies.map((reply) => (
                <button
                  key={reply.text}
                  type="button"
                  onClick={() => handleQuickReply(reply.text)}
                  className="rounded-full border border-zinc-300 bg-white px-6 py-3 text-[1.3rem] font-medium text-zinc-800 transition-colors hover:bg-zinc-100 active:bg-zinc-100"
                >
                  {reply.text}
                </button>
              ))}
        </div>
      </div>
      <AddTrackingModal
        isOpen={!!pendingReply}
        dismiss={() => setPendingReply(null)}
        order={orderDoc ?? undefined}
        order_item={pendingReply?.item}
        onSuccess={() => {
          if (pendingReply) onQuickReply(pendingReply.text);
          setPendingReply(null);
        }}
      />
    </>
  );
}
